import {
  IconEllipsisV,
  IconEnvelope,
  IconPhone,
  IconPlus,
  IconUserCircle,
} from '@maia/icons';
import { ButtonMenu, PageHeader, showToast, Subnav, Tag } from '@maia/ui';
import {
  createFileRoute,
  linkOptions,
  Outlet,
  useNavigate,
} from '@tanstack/react-router';
import { validateSearch } from '../../../../utils/pagination';
import { getCustomerOptions } from '../../../../features/customer/shared/customer-query-options';
import { fileToBase64, RefProvider } from '@maia/utils';
import { useDrawerStore } from './../../../../stores/drawer.store';
import { NoteSidedrawer } from './../../../../features/shared/note/note-sidedrawer';
import { AddNoteSidedrawer } from './../../../../features/shared/note/add-note-sidedrawer';
import { ActivitySidedrawer } from './../../../../features/shared/activity/activity-sidedrawer';
import { IAttachmentEntity } from '@maia/shared';
import { useCreateCustomerNote } from './../../../../features/customer/notes/create-note.options';
import { queryClient } from './../../../../main';
import { useMutation } from '@tanstack/react-query';
import { service } from '@maia/customer';
import Cookies from 'js-cookie';
import { TaskSidedrawer } from './../../../../features/shared/task/task-sidedrawer';
import { InsuranceSidedrawer } from './../../../../features/insurance/insurance/insurance-sidedrawer';

export const Route = createFileRoute('/_authenticated/customers/$guid/_layout')(
  {
    component: Layout,
    loaderDeps: ({ search: { page } }) => ({ page }),
    loader: ({ context: { queryClient }, deps: { page }, params: { guid } }) =>
      queryClient.ensureQueryData(getCustomerOptions(guid, { page })),
    validateSearch: validateSearch,
  },
);

function Layout() {
  const data = Route.useLoaderData();
  const { guid } = Route.useParams();
  const { openDrawer, closeDrawer } = useDrawerStore();

  const params = Route.useParams();
  const navigate = useNavigate();

  const { mutateAsync: remove, isPending } = useMutation({
    mutationFn: (params: Parameters<typeof service.remove>[number]) =>
      service.remove(params),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ['customers'],
      });

      if (response.response?.token) {
        Cookies.set('auth_tokens', JSON.stringify(response.response.token), {
          secure: true,
          sameSite: 'strict',
          expires: new Date(response.response.token.refreshTokenExpiresAt),
        });
      }

      showToast({
        intent: 'success',
        title: 'Klant verwijderd',
        config: { position: 'top-right' },
      });

      navigate({
        to: '/customers',
      });
    },
    onError: () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden de klant niet verwijderen. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  });

  const { mutateAsync: createNote } = useCreateCustomerNote(
    data.guid,
    () => {
      queryClient.invalidateQueries({
        queryKey: ['notes', data.guid],
      });
      showToast({
        intent: 'success',
        title: 'Notitie toegevoegd',
        config: { position: 'top-right' },
      });
      closeDrawer();
    },
    () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden niet de notitie voor je aanmaken. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  );

  const handleImpersonateClick = async (email: string) => {
    const result = await service.impersonate(email);

    if (result.type === 'ok') {
      window.open(result.data, '_blank', 'noopener,noreferrer');
    } else {
      console.error(result.error);
    }
  };

  return (
    <RefProvider<HTMLDivElement>>
      {({ ref, trackedElement }) => (
        <div className="layout-sidebar relative" ref={ref}>
          <PageHeader
            icon={IconUserCircle}
            title={data.consumer.fullName}
            specs={[
              { icon: IconEnvelope, text: data.consumer.email },
              { icon: IconPhone, text: data.consumer.phone ?? '' },
            ]}
            reference={`#${data.reference}`}
            actions={
              <>
                <ButtonMenu
                  intent="tertiary"
                  size="default"
                  iconRight={IconEllipsisV}
                  isDisabled={isPending}
                  isLoading={isPending}
                  menuItems={[
                      {
                      icon: 'signin',
                      text: 'Login als klant',
                      // navigate to the customer portal with the guid and a token to impersonate the customer
                      onAction: () => {
                        void handleImpersonateClick(data.guid ?? '');
                      },
                    },
                    {
                      icon: 'pen',
                      text: 'Klant wijzigen',
                      onAction: () =>
                        navigate({
                          to: '/customers/$guid/edit',
                          params: { guid: params.guid },
                        }),
                    },
                    ...(data.isDeletable
                      ? [
                          {
                            icon: 'trash-alt',
                            text: 'Klant verwijderen',
                            onAction: () =>
                              remove({
                                guid: params.guid,
                                email: data.consumer.email,
                              }),
                            intent: 'destructive',
                          } as const,
                        ]
                      : []),
                  ]}
                >
                  Acties
                </ButtonMenu>
                <ButtonMenu
                  intent="cta"
                  size="default"
                  iconLeft={IconPlus}
                  isDisabled={isPending}
                  menuItems={[
                    {
                      icon: 'receipt',
                      text: 'Nieuwe verzekering',
                      onAction: () =>
                        navigate({
                          to: '/add-insurance/$customer',
                          params: { customer: guid },
                        }),
                    },
                    {
                      icon: 'check-square',
                      text: 'Nieuwe taak',
                      onAction: () =>
                        navigate({
                          to: '/tasks/new/customer',
                          search: { guid: data.guid },
                        }),
                    },
                    {
                      icon: 'notes',
                      text: 'Nieuwe notitie',
                      onAction: () => openDrawer({ type: 'note.add' }),
                    },
                  ]}
                >
                  Toevoegen
                </ButtonMenu>
              </>
            }
            status={<Tag {...data.status} />}
            className="area-page-header"
          />

          <Subnav
            className="area-page-subnav"
            links={[
              linkOptions({
                to: '/customers/$guid',
                params: {
                  guid,
                },
                label: 'Overzicht',
              }),
              linkOptions({
                to: '/customers/$guid/insurances',
                params: {
                  guid,
                },
                label: 'Verzekeringen',
              }),
              linkOptions({
                to: '/customers/$guid/activities',
                params: {
                  guid,
                },
                label: 'Activiteit',
              }),
              linkOptions({
                to: '/customers/$guid/claims',
                params: {
                  guid,
                },
                label: 'Claims',
              }),
              linkOptions({
                to: '/customers/$guid/tasks',
                params: {
                  guid,
                },
                label: 'Taken',
              }),
              linkOptions({
                to: '/customers/$guid/invoices',
                params: {
                  guid,
                },
                label: 'Facturen',
              }),
              linkOptions({
                to: '/customers/$guid/quotations',
                params: {
                  guid,
                },
                label: 'Offertes',
              }),
              linkOptions({
                to: '/customers/$guid/notes',
                params: {
                  guid,
                },
                label: 'Notities',
              }),
            ]}
          />

          <div className="area-page-content content-grid">
            <Outlet />
          </div>

          <InsuranceSidedrawer portalRef={trackedElement} />
          <TaskSidedrawer portalRef={trackedElement} />
          <ActivitySidedrawer portalRef={trackedElement} />
          <NoteSidedrawer portalRef={trackedElement} />
          <AddNoteSidedrawer
            portalRef={trackedElement}
            onSubmit={async (values) => {
              const files: Array<IAttachmentEntity> = await Promise.all(
                values.explanation.files.map(async (file) => {
                  const base64 = await fileToBase64(file);

                  return {
                    filename: file.name,
                    mimetype: file.type,
                    encoded_document: base64,
                  };
                }),
              );

              await createNote({
                category: values.type,
                title: values.subject,
                description: values.explanation.markdown,
                attachments: files,
              });
            }}
          />
        </div>
      )}
    </RefProvider>
  );
}
